/**
 * 🤝 Referral v2 夥伴渠道區（2026-09-19，coder-brief-referral-v2.md W5）：
 * ①邀請連結（token 生成/revoke）②待審批（批准＝發 magic link 按鈕＋溫馨提示，D7）
 * ③渠道漏斗＋收款對數（split_snapshot 凍結行）。經 gateway same-origin /api/referrals/admin/v2/*。
 */
import { useCallback, useEffect, useState } from "react";
import { waFetchV2 as waFetch } from "../lib/api";

const fmt = (ms?: number | null) => (ms ? new Date(ms).toLocaleDateString("zh-HK") : "—");
/* v64（2026-10-26）：佣金捨入到分——SUM 浮點塵（382.28000000000003）不再外露 */
const hk = (n?: number | null) => Math.round((Number(n) || 0) * 100) / 100;

export default function ReferralV2Section() {
  const [partner, setPartner] = useState("eid");
  const [invites, setInvites] = useState<{ token: string; created_at: number; revoked_at: number | null; joined: number }[]>([]);
  const [funnel, setFunnel] = useState<{ id: number; name: string; code: string; status: string; activated_at: number | null; phone: string; clicks: number; leads: number; converted: number; rewards_total: number }[]>([]);
  const [orders, setOrders] = useState<{ order_id: string; customer_name: string | null; plan: string; amount: number | null; status: string; ref_code: string | null; split_snapshot: string | null; created_at: number }[]>([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const p = encodeURIComponent(partner);
      const [inv, ov] = await Promise.all([
        waFetch(`/api/referrals/admin/v2/invites?partner_id=${p}`),
        waFetch(`/api/referrals/admin/v2/channel-overview?partner_id=${p}`),
      ]);
      if (inv.ok) setInvites((await inv.json()).invites || []);
      if (ov.ok) {
        const j = await ov.json();
        setFunnel(j.funnel || []);
        setOrders(j.orders || []);
      }
    } catch (e) {
      setMsg("載入失敗：" + String(e).slice(0, 100));
    }
    setBusy(false);
  }, [partner]);

  useEffect(() => { load(); }, [load]);

  const genInvite = async () => {
    setMsg("");
    const res = await waFetch("/api/referrals/admin/v2/invites", { method: "POST", body: JSON.stringify({ partner_id: partner }) });
    const j = await res.json().catch(() => ({}));
    if (res.ok && j.join_url) {
      try { await navigator.clipboard.writeText(j.join_url); setMsg("✅ 邀請連結已生成＋複製：" + j.join_url); }
      catch { setMsg("✅ 邀請連結：" + j.join_url); }
      load();
    } else setMsg("生成失敗：" + (j.error || res.status));
  };
  const revoke = async (token: string) => {
    if (!confirm("收回這條邀請連結？（已開戶 agent 不受影響）")) return;
    await waFetch(`/api/referrals/admin/v2/invites/${encodeURIComponent(partner)}/${token}`, { method: "DELETE" });
    load();
  };
  const resendMagic = async (id: number) => {
    if (!confirm("重發後台登入連結到此夥伴的 WhatsApp？")) return;
    const res = await waFetch("/api/referrals/admin/v2/resend-magic", { method: "POST", body: JSON.stringify({ referrer_id: id }) });
    if (res.ok) { setMsg("✅ 登入連結已重發到夥伴 WhatsApp（15 分鐘內有效）"); } else { setMsg("❌ 重發失敗"); }
  };
  const approve = async (id: number) => {
    setMsg("");
    const res = await waFetch("/api/referrals/admin/v2/approve", { method: "POST", body: JSON.stringify({ referrer_id: id }) });
    if (res.ok) { setMsg("✅ 已批准——magic link 按鈕訊息已發送（溫馨提示：登入後按「開戶完成」收通知）"); load(); }
    else setMsg("批准失敗：" + res.status);
  };
  const copyJoin = (code: string) => {
    navigator.clipboard?.writeText(`https://wtsaibot.com/r/${partner}/${code}`).catch(() => {});
    setMsg("📋 已複製 " + code + " 的分享連結");
  };

  /* W-14：DEMO 模擬成交單清理——先 GET 預覽，confirm 後 POST 還原（只掃 customer_name DEMO* 帶 snapshot） */
  const demoCleanup = async () => {
    setMsg("");
    const prev = await waFetch("/api/referrals/admin/v2/demo-cleanup");
    const pj = await prev.json().catch(() => ({ count: -1 }));
    if (!prev.ok) { setMsg("❌ 預覽失敗：" + (pj.error || prev.status)); return; }
    if (!pj.count) { setMsg("✅ 現時沒有 DEMO 模擬單（無需清理）"); return; }
    const ids = (pj.orders || []).map((o: { id: string }) => o.id).join("、");
    if (!confirm(`清理 ${pj.count} 筆 DEMO 模擬成交單（連佣金行）？\n只影響 customer_name 以 DEMO 開頭的訂單，真實單不受影響。\n${ids.slice(0, 200)}`)) return;
    const res = await waFetch("/api/referrals/admin/v2/demo-cleanup", { method: "POST", body: JSON.stringify({ confirm: true }) });
    const j = await res.json().catch(() => ({}));
    if (res.ok && j.ok) setMsg(`🧹 已清理：訂單 ${j.deleted_orders} 筆＋佣金行 ${j.deleted_rewards} 筆`);
    else setMsg("❌ 清理失敗：" + (j.error || res.status));
    load();
  };

  const pending = funnel.filter((f) => f.status !== "active");
  const th = "px-3 py-2 text-left text-[11px] uppercase tracking-wide text-slate-500 font-semibold";
  const td = "px-3 py-2.5 text-sm text-slate-300";

  return (
    <div className="mt-6 rounded-xl border border-slate-700/60 bg-slate-900/40 p-5">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h2 className="text-base font-bold text-slate-200">🤝 夥伴渠道（Referral v2）</h2>
        <input value={partner} onChange={(e) => setPartner(e.target.value.replace(/[^a-z0-9_-]/gi, "").toLowerCase())}
          className="w-28 rounded-lg border border-slate-600 bg-slate-800 px-3 py-1.5 text-sm text-slate-100" placeholder="partner id" />
        <button onClick={load} disabled={busy} className="rounded-lg bg-slate-700 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-600">🔄</button>
        <button onClick={demoCleanup} title="清理 customer_name 以 DEMO 開頭的模擬成交單（W3 演示數據還原）" className="rounded-lg bg-rose-900/50 border border-rose-700/50 px-3 py-1.5 text-xs text-rose-200 hover:bg-rose-900/70">🧹 DEMO 單清理</button>
        {msg && <span className="text-xs text-emerald-400">{msg}</span>}
      </div>

      {/* ① 邀請連結 */}
      <div className="mb-5">
        <div className="flex items-center gap-3 mb-2">
          <h3 className="text-sm font-semibold text-slate-300">🔗 邀請連結（派畀 Agent 開戶）</h3>
          <button onClick={genInvite} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-500">＋ 生成新 token</button>
        </div>
        {invites.length === 0 ? <p className="text-xs text-slate-500">尚未生成——生成後把 /join/{partner}?k=… 連結派給銷售。</p> : (
          <table className="w-full"><tbody>
            {invites.map((i) => (
              <tr key={i.token} className="border-b border-slate-800">
                <td className={td + " font-mono text-xs"}>/join/{partner}?k={i.token.slice(0, 10)}…</td>
                <td className={td}>建立 {fmt(i.created_at)}</td>
                <td className={td}>{i.revoked_at ? <span className="text-rose-400">已收回</span> : <span className="text-emerald-400">生效中 · {i.joined} 人已加入</span>}</td>
                <td className={td}>{!i.revoked_at && <button onClick={() => revoke(i.token)} className="rounded bg-rose-900/40 px-2 py-1 text-xs text-rose-300">收回</button>}</td>
              </tr>
            ))}
          </tbody></table>
        )}
      </div>

      {/* ② 待審批 */}
      <div className="mb-5">
        <h3 className="text-sm font-semibold text-slate-300 mb-2">⏳ 待審批（{pending.length}）</h3>
        {pending.length === 0 ? <p className="text-xs text-slate-500">無待審 Agent。</p> : (
          <table className="w-full"><tbody>
            {pending.map((f) => (
              <tr key={f.id} className="border-b border-slate-800">
                <td className={td}><b>{f.name}</b>（{f.phone.slice(0, 4)}…{f.phone.slice(-2)}）</td>
                <td className={td + " font-mono text-xs"}>{f.code}</td>
                <td className={td}>開戶 {fmt(f.activated_at)}</td>
                <td className={td}>
                  {f.status === "pending" ? (
                    <button onClick={() => approve(f.id)} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-500">✅ 批准（發 magic link）</button>
                  ) : (
                    <button onClick={() => resendMagic(f.id)} className="rounded-lg bg-sky-600/80 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-500">🔑 重發登入連結</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody></table>
        )}
      </div>

      {/* ③ 渠道漏斗＋收款對數 */}
      <div className="mb-5">
        <h3 className="text-sm font-semibold text-slate-300 mb-2">📊 渠道漏斗</h3>
        {funnel.length === 0 ? <p className="text-xs text-slate-500">無 Agent。</p> : (
          <table className="w-full mb-4"><thead><tr><th className={th}>Agent</th><th className={th}>碼</th><th className={th}>點擊</th><th className={th}>查詢</th><th className={th}>成交</th><th className={th}>佣金</th><th className={th}>分享連結</th></tr></thead><tbody>
            {funnel.map((f) => (
              <tr key={f.id} className="border-b border-slate-800">
                <td className={td}>{f.name}</td><td className={td + " font-mono text-xs"}>{f.code}</td>
                <td className={td}>{f.clicks}</td><td className={td}>{f.leads}</td><td className={td}>{f.converted}</td>
                <td className={td}><b>HK${hk(f.rewards_total)}</b></td>
                <td className={td}><button onClick={() => copyJoin(f.code)} className="rounded bg-slate-700 px-2 py-1 text-xs text-slate-200">📋</button></td>
              </tr>
            ))}
          </tbody></table>
        )}
        <h3 className="text-sm font-semibold text-slate-300 mb-2">💰 渠道收款對數（split 收款時凍結）</h3>
        {orders.length === 0 ? <p className="text-xs text-slate-500">暫無渠道訂單。</p> : (
          <table className="w-full"><thead><tr><th className={th}>單號</th><th className={th}>客人</th><th className={th}>套餐</th><th className={th}>實收</th><th className={th}>歸屬拆帳</th><th className={th}>Agent</th><th className={th}>狀態</th></tr></thead><tbody>
            {orders.map((o) => {
              let s: { agent_hkd?: number; org_hkd?: number; aibot_hkd?: number } = {};
              try { s = o.split_snapshot ? JSON.parse(o.split_snapshot) : {}; } catch { /* ignore */ }
              return (
                <tr key={o.order_id} className="border-b border-slate-800">
                  <td className={td + " font-mono text-xs"}>{o.order_id}</td>
                  <td className={td}>{o.customer_name || "—"}</td>
                  <td className={td}>{o.plan === "pro499" ? "Pro" : "Lite"}</td>
                  <td className={td}><b>HK${o.amount ?? "—"}</b></td>
                  <td className={td + " text-xs"}>{s.agent_hkd !== undefined ? `agent HK$${hk(s.agent_hkd)}｜org HK$${hk(s.org_hkd)}｜AIBot HK$${hk(s.aibot_hkd)}` : "—"}</td>
                  <td className={td + " font-mono text-xs"}>{o.ref_code || "—"}</td>
                  <td className={td}>{o.status}</td>
                </tr>
              );
            })}
          </tbody></table>
        )}
      </div>
    </div>
  );
}
